import type { ConnectionQuality, LiveMediaAdapter, LiveMediaState, LiveSessionCredential } from './LiveMediaAdapter';

interface JanusResponse {
  janus: string;
  transaction?: string;
  data?: { id?: number };
  error?: { reason?: string };
  jsep?: RTCSessionDescriptionInit;
  plugindata?: { data?: { audiobridge?: string; error?: string } };
}

export class JanusLiveMediaAdapter implements LiveMediaAdapter {
  #credential: LiveSessionCredential | null = null;
  #peer: RTCPeerConnection | null = null;
  #stream: MediaStream | null = null;
  #monitor: MediaStream | null = null;
  #audio: HTMLAudioElement | null = null;
  #sessionId: number | null = null;
  #handleId: number | null = null;
  #listeners = new Set<(state: LiveMediaState) => void>();
  #statsTimer = 0;
  #keepaliveTimer = 0;
  #audioContext: AudioContext | null = null;
  #analyser: AnalyserNode | null = null;
  #microphoneLevel = 0;

  public async connect(credential: LiveSessionCredential): Promise<void> {
    await this.disconnect();
    this.#credential = credential;
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { channelCount: 1, echoCancellation: false, noiseSuppression: false, autoGainControl: false },
      video: false,
    });
    this.#stream = stream;
    stream.getAudioTracks().forEach((track) => { track.enabled = false; });
    this.#beginMeter(stream);

    const peer = new RTCPeerConnection({ iceServers: credential.iceServers, bundlePolicy: 'max-bundle' });
    this.#peer = peer;
    stream.getTracks().forEach((track) => peer.addTrack(track, stream));
    peer.addEventListener('connectionstatechange', () => this.#publish());
    peer.addEventListener('track', (event) => {
      this.#monitor = event.streams[0] ?? new MediaStream([event.track]);
      const audio = this.#audio ?? new Audio();
      audio.autoplay = true;
      audio.setAttribute('playsinline', '');
      audio.srcObject = this.#monitor;
      this.#audio = audio;
      void audio.play().catch(() => undefined);
      this.#publish();
    });

    if (credential.signalingMode === 'guest-proxy') {
      if (credential.providerSessionId === undefined || credential.providerHandleId === undefined) throw new Error('Не вистачає даних для підключення гостьового аудіоканалу.');
      this.#sessionId = credential.providerSessionId;
      this.#handleId = credential.providerHandleId;
    } else {
      const session = await this.#janus('', { janus: 'create', transaction: transaction() });
      this.#sessionId = requiredId(session, 'session');
      const attached = await this.#janus(`/${this.#sessionId}`, { janus: 'attach', plugin: 'janus.plugin.audiobridge', transaction: transaction() });
      this.#handleId = requiredId(attached, 'handle');
    }

    const offer = await peer.createOffer({ offerToReceiveAudio: true });
    await peer.setLocalDescription(offer);
    await waitForIceGathering(peer, 4_000);
    const localDescription = peer.localDescription;
    if (localDescription === null) throw new Error('Браузер не зміг підготувати захищене WebRTC-з’єднання.');
    const requestTransaction = transaction();
    const response = await this.#janus(`/${this.#sessionId}/${this.#handleId}`, {
      janus: 'message', transaction: requestTransaction,
      body: {
        request: 'join', room: credential.roomId, id: credential.janusParticipantId,
        display: credential.displayName, group: credential.group, token: credential.token,
        muted: false, quality: 4, expected_loss: 3,
      },
      // Janus otherwise assumes more trickle candidates will follow. This
      // client intentionally sends the bounded set gathered above in one SDP.
      jsep: { type: localDescription.type, sdp: localDescription.sdp, trickle: false },
    });
    const joined = response.janus === 'ack' ? await this.#poll(requestTransaction) : response;
    if (joined.jsep === undefined || joined.plugindata?.data?.audiobridge !== 'joined') {
      throw new Error(joined.plugindata?.data?.error ?? 'Сервер аудіозв’язку не прийняв мікрофон ведучого.');
    }
    await peer.setRemoteDescription(joined.jsep);
    this.#statsTimer = window.setInterval(() => { void this.#sampleStats(); }, 2_000);
    this.#keepaliveTimer = window.setInterval(() => {
      void this.#keepalive().catch(() => this.#publish());
    }, 20_000);
    this.#publish();
  }

  public async setMicrophoneEnabled(enabled: boolean): Promise<void> {
    const track = this.#stream?.getAudioTracks()[0];
    if (track === undefined) throw new Error('Microphone is not connected.');
    track.enabled = enabled;
    this.#publish();
  }

  public async setRoutingGroup(group: 'onair' | 'backstage'): Promise<void> {
    if (this.#sessionId === null || this.#handleId === null || this.#credential === null) throw new Error('Microphone is not connected.');
    const requestTransaction = transaction();
    const response = await this.#janus(`/${this.#sessionId}/${this.#handleId}`, {
      janus: 'message', transaction: requestTransaction, body: { request: 'configure', room: this.#credential.roomId, group },
    });
    if (response.janus === 'ack') await this.#poll(requestTransaction);
  }

  public subscribe(listener: (state: LiveMediaState) => void): () => void {
    this.#listeners.add(listener);
    listener(this.#state());
    return () => this.#listeners.delete(listener);
  }

  public async disconnect(): Promise<void> {
    window.clearInterval(this.#statsTimer);
    window.clearInterval(this.#keepaliveTimer);
    this.#statsTimer = 0;
    this.#keepaliveTimer = 0;
    const session = this.#sessionId;
    if (session !== null && this.#credential !== null) {
      try { await this.#janus(`/${session}`, { janus: 'destroy', transaction: transaction() }); } catch { /* expires server-side */ }
    }
    this.#peer?.close();
    this.#peer = null;
    this.#stream?.getTracks().forEach((track) => track.stop());
    this.#stream = null;
    this.#monitor?.getTracks().forEach((track) => track.stop());
    this.#monitor = null;
    if (this.#audio !== null) { this.#audio.pause(); this.#audio.srcObject = null; }
    this.#audio = null;
    await this.#audioContext?.close().catch(() => undefined);
    this.#audioContext = null;
    this.#analyser = null;
    this.#microphoneLevel = 0;
    this.#sessionId = null;
    this.#handleId = null;
    this.#credential = null;
    this.#publish();
  }

  async #keepalive(): Promise<void> {
    if (this.#sessionId === null) return;
    await this.#janus(`/${this.#sessionId}`, { janus: 'keepalive', transaction: transaction() });
  }

  async #poll(expectedTransaction: string): Promise<JanusResponse> {
    if (this.#sessionId === null || this.#credential === null) throw new Error('Janus session is missing.');
    const deadline = Date.now() + 8_000;
    while (Date.now() < deadline) {
      const directToken = this.#credential.signalingMode === 'guest-proxy' ? '' : `&token=${encodeURIComponent(this.#credential.token)}`;
      const url = this.#url(`/${this.#sessionId}?rid=${Date.now()}&maxev=1${directToken}`);
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', headers: this.#headers(false) });
      const event = await response.json() as JanusResponse;
      // Ignore unrelated events sharing the Janus session queue.
      if (event.transaction === expectedTransaction) return checked(event);
    }
    throw new Error('Janus negotiation timed out.');
  }

  async #janus(path: string, payload: Record<string, unknown>): Promise<JanusResponse> {
    const credential = this.#credential;
    const response = await fetch(this.#url(path), {
      method: 'POST', credentials: 'same-origin', cache: 'no-store',
      headers: this.#headers(true),
      body: JSON.stringify(credential === null || credential.signalingMode === 'guest-proxy' ? payload : { ...payload, token: credential.token }),
    });
    if (!response.ok) throw new Error(`Janus signaling failed (${response.status}).`);
    return checked(await response.json() as JanusResponse);
  }

  #url(path: string): string {
    if (this.#credential === null) throw new Error('Відсутні дані доступу до прямого ефіру.');
    return new URL(this.#credential.signalingUrl + path, window.location.origin).toString();
  }

  #headers(json: boolean): Record<string, string> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (json) headers['Content-Type'] = 'application/json';
    if (this.#credential?.signalingMode === 'guest-proxy') headers.Authorization = `Guest ${this.#credential.token}`;
    return headers;
  }

  #beginMeter(stream: MediaStream): void {
    const AudioContextClass = window.AudioContext;
    const context = new AudioContextClass();
    const analyser = context.createAnalyser();
    analyser.fftSize = 256;
    context.createMediaStreamSource(stream).connect(analyser);
    this.#audioContext = context;
    this.#analyser = analyser;
  }

  async #sampleStats(): Promise<void> {
    const peer = this.#peer;
    if (peer === null) return;
    let latency: number | null = null;
    let jitter: number | null = null;
    let loss: number | null = null;
    const stats = await peer.getStats();
    stats.forEach((report) => {
      if (report.type === 'remote-inbound-rtp' && report.kind === 'audio') {
        if (typeof report.roundTripTime === 'number') latency = report.roundTripTime * 1_000;
        if (typeof report.jitter === 'number') jitter = report.jitter * 1_000;
        if (typeof report.fractionLost === 'number') loss = report.fractionLost * 100;
      }
      if (report.type === 'candidate-pair' && report.state === 'succeeded' && typeof report.currentRoundTripTime === 'number') {
        latency ??= report.currentRoundTripTime * 1_000;
      }
    });
    const analyser = this.#analyser;
    if (analyser !== null) {
      const values = new Uint8Array(analyser.fftSize);
      analyser.getByteTimeDomainData(values);
      const power = values.reduce((sum, sample) => sum + ((sample - 128) / 128) ** 2, 0) / values.length;
      this.#microphoneLevel = Math.min(1, Math.sqrt(power) * 4);
    }
    this.#lastStats = { latencyMs: latency, jitterMs: jitter, packetLossPercent: loss };
    this.#publish();
  }

  #lastStats: { latencyMs: number | null; jitterMs: number | null; packetLossPercent: number | null } = { latencyMs: null, jitterMs: null, packetLossPercent: null };

  #state(): LiveMediaState {
    const { latencyMs, jitterMs, packetLossPercent } = this.#lastStats;
    return {
      connection: this.#peer?.connectionState ?? 'closed',
      quality: classify(latencyMs, jitterMs, packetLossPercent, this.#peer?.connectionState ?? 'closed'),
      microphoneLevel: this.#microphoneLevel,
      latencyMs, jitterMs, packetLossPercent,
      monitorStream: this.#monitor,
    };
  }

  #publish(): void {
    const state = this.#state();
    this.#listeners.forEach((listener) => listener(state));
  }
}

function checked(response: JanusResponse): JanusResponse {
  if (response.janus === 'error') throw new Error(response.error?.reason ?? 'Janus rejected the request.');
  return response;
}

function requiredId(response: JanusResponse, kind: string): number {
  const id = response.data?.id;
  if (response.janus !== 'success' || typeof id !== 'number') throw new Error(`Janus ${kind} creation failed.`);
  return id;
}

function transaction(): string {
  return crypto.randomUUID().replaceAll('-', '').slice(0, 24);
}

function waitForIceGathering(peer: RTCPeerConnection, timeoutMs: number): Promise<void> {
  if (peer.iceGatheringState === 'complete') return Promise.resolve();
  return new Promise((resolve) => {
    const timer = window.setTimeout(done, timeoutMs);
    function done(): void { window.clearTimeout(timer); peer.removeEventListener('icegatheringstatechange', changed); resolve(); }
    function changed(): void { if (peer.iceGatheringState === 'complete') done(); }
    peer.addEventListener('icegatheringstatechange', changed);
  });
}

function classify(latency: number | null, jitter: number | null, loss: number | null, connection: RTCPeerConnectionState): ConnectionQuality {
  if (connection !== 'connected' || latency === null || jitter === null || loss === null) return connection === 'connecting' ? 'GOOD' : 'POOR';
  if (latency < 100 && loss < 1 && jitter < 20) return 'EXCELLENT';
  if (latency < 200 && loss < 3 && jitter < 40) return 'GOOD';
  if (latency < 400 && loss < 8) return 'UNSTABLE';
  return 'POOR';
}
